import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { callApi } from '@/hooks/useFetch'

export interface MonitoredSource {
  id: string
  organization_id: string
  name: string
  feed_url: string
  is_active: boolean
  last_fetched_at?: string | null
  created_at?: string | null
}

export interface DiscoveredStartup {
  id: string
  name: string
  name_chinese?: string | null
  sector?: string | null
  location?: string | null
  source_url?: string | null
  source_article_url: string
  source_id?: string | null
  source_name?: string | null
  kind: 'new' | 'existing'
  event_type?: string | null
  matched_startup_id?: string | null
  dimension_problem_market_text?: string | null
  dimension_business_execution_text?: string | null
  summary?: string | null
  status: string
  created_startup_id?: string | null
  created_at?: string | null
}

// --- Monitored sources ----------------------------------------------------
// Two scopes share one implementation: superadmin manages any org by id
// (/superadmin/organizations/{orgId}/monitored-sources), org admins manage
// their own org (/monitoring/sources, org derived from the auth token).

interface SourceScope {
  base: string
  key: readonly unknown[]
  enabled?: boolean
}

const adminScope = (orgId: string): SourceScope => ({
  base: `/superadmin/organizations/${orgId}/monitored-sources`,
  key: ['monitoredSources', orgId],
  enabled: !!orgId,
})
const ORG_SCOPE: SourceScope = { base: '/monitoring/sources', key: ['orgMonitoredSources'] }

function useSourcesQuery(scope: SourceScope) {
  const { data, isLoading, error, refetch } = useQuery<MonitoredSource[]>({
    queryKey: scope.key as unknown[],
    queryFn: () => callApi(scope.base),
    enabled: scope.enabled ?? true,
  })
  return { sources: data ?? [], isLoading, error: error as Error | null, refetch }
}

function useSourceMutation<TArgs>(scope: SourceScope, fn: (a: TArgs) => Promise<unknown>) {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: scope.key as unknown[] }),
  })
  return { mutateAsync, isPending }
}

const useCreateFor = (scope: SourceScope) =>
  useSourceMutation(scope, (payload: { name: string; feed_url: string }) =>
    callApi(scope.base, { method: 'POST', data: payload })
  )
const useUpdateFor = (scope: SourceScope) =>
  useSourceMutation(
    scope,
    ({ sourceId, ...data }: { sourceId: string; name?: string; feed_url?: string; is_active?: boolean }) =>
      callApi(`${scope.base}/${sourceId}`, { method: 'PATCH', data })
  )
const useDeleteFor = (scope: SourceScope) =>
  useSourceMutation(scope, (sourceId: string) =>
    callApi(`${scope.base}/${sourceId}`, { method: 'DELETE' })
  )

// Superadmin-scoped (by orgId) — used on the org-detail page.
export const useMonitoredSources = (orgId: string) => useSourcesQuery(adminScope(orgId))
export function useCreateMonitoredSource(orgId: string) {
  const { mutateAsync, isPending } = useCreateFor(adminScope(orgId))
  return { createSource: mutateAsync, isCreating: isPending }
}
export function useUpdateMonitoredSource(orgId: string) {
  const { mutateAsync, isPending } = useUpdateFor(adminScope(orgId))
  return { updateSource: mutateAsync, isUpdating: isPending }
}
export function useDeleteMonitoredSource(orgId: string) {
  const { mutateAsync, isPending } = useDeleteFor(adminScope(orgId))
  return { deleteSource: mutateAsync, isDeleting: isPending }
}

// Self-scoped (current org) — used on the org-admin sources page.
export const useOrgMonitoredSources = () => useSourcesQuery(ORG_SCOPE)
export function useCreateOrgSource() {
  const { mutateAsync, isPending } = useCreateFor(ORG_SCOPE)
  return { createSource: mutateAsync, isCreating: isPending }
}
export function useUpdateOrgSource() {
  const { mutateAsync, isPending } = useUpdateFor(ORG_SCOPE)
  return { updateSource: mutateAsync, isUpdating: isPending }
}
export function useDeleteOrgSource() {
  const { mutateAsync, isPending } = useDeleteFor(ORG_SCOPE)
  return { deleteSource: mutateAsync, isDeleting: isPending }
}

// --- Sync triggers ---------------------------------------------------------

function usePostMutation(url: string) {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: () => callApi(url, { method: 'POST' }),
  })
  return { runSync: mutateAsync, isRunning: isPending }
}
// Global scheduled sync (superadmin) vs this-org-only sync (org admin).
export const useRunDailySync = () => usePostMutation('/monitoring/run-daily-sync')
export const useRunOrgSync = () => usePostMutation('/monitoring/sources/sync')

// --- Discovered-startup review queue --------------------------------------

export function useDiscoveredStartups(status = 'pending') {
  const { data, isLoading, error, refetch } = useQuery<DiscoveredStartup[]>({
    queryKey: ['discoveredStartups', status],
    queryFn: () => callApi(`/monitoring/discovered-startups?status=${status}`),
  })
  return { discovered: data ?? [], isLoading, error: error as Error | null, refetch }
}

// Count of pending discoveries for the navbar review badge. Shares the
// ['discoveredStartups','pending'] cache with useDiscoveredStartups, and is
// gated by `enabled` so only roles that can see Discovery hit the endpoint.
export function usePendingDiscoveryCount(enabled = true) {
  const { data } = useQuery<DiscoveredStartup[]>({
    queryKey: ['discoveredStartups', 'pending'],
    queryFn: () => callApi('/monitoring/discovered-startups?status=pending'),
    enabled,
    staleTime: 60_000,
  })
  return data?.length ?? 0
}

export function useDiscoveredStartup(id: string) {
  const { data, isLoading, error, refetch } = useQuery<DiscoveredStartup>({
    queryKey: ['discoveredStartup', id],
    queryFn: () => callApi(`/monitoring/discovered-startups/${id}`),
    enabled: !!id,
  })
  return { discovered: data ?? null, isLoading, error: error as Error | null, refetch }
}

export function useApproveDiscovered() {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (discoveredId: string) =>
      callApi(`/monitoring/discovered-startups/${discoveredId}/approve`, {
        method: 'POST',
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['discoveredStartups'] }),
  })
  return { approve: mutateAsync, isApproving: isPending }
}

export function useGenerateStartupOpportunity() {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({ startupId, newsNote }: { startupId: string; newsNote?: string }) =>
      callApi(`/monitoring/startups/${startupId}/generate-opportunity`, {
        method: 'POST',
        data: { news_note: newsNote ?? null },
      }),
  })
  return { generateOpportunity: mutateAsync, isGenerating: isPending }
}

export function useGenerateDiscoveredOpportunity() {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({
      discoveredId,
      newsNote,
    }: {
      discoveredId: string
      newsNote?: string
    }) =>
      callApi(`/monitoring/discovered-startups/${discoveredId}/generate-opportunity`, {
        method: 'POST',
        data: { news_note: newsNote ?? null },
      }),
  })
  return { generateOpportunity: mutateAsync, isGenerating: isPending }
}

export function useRejectDiscovered() {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (discoveredId: string) =>
      callApi(`/monitoring/discovered-startups/${discoveredId}/reject`, {
        method: 'POST',
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['discoveredStartups'] }),
  })
  return { reject: mutateAsync, isRejecting: isPending }
}
